import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarClock,
  Camera,
  Check,
  CreditCard,
  Image as ImageIcon,
  LayoutDashboard,
  LogOut,
  MapPin,
  Receipt,
  Sparkles,
  User as UserIcon,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { absolutePhotoUrl } from "@/lib/adminApi";

const PLAN_LABEL = { monthly: "Monthly", biweekly: "Bi-Weekly", onetime: "One-Time" };
const PLAN_PRICE = {
  monthly: { perMo: 25, suffix: "/ mo · up to 2 bins" },
  biweekly: { perBin: 30, suffix: "/ mo · per bin" },
  onetime: { perBin: 40, suffix: "/ bin · single clean" },
};

const SERVICE_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const EXTRA_BIN_PRICE = 8;

function planMonthlyEstimate(planCode, bins) {
  if (planCode === "monthly")
    return 25 + Math.max(0, Math.max(1, bins) - 2) * EXTRA_BIN_PRICE;
  if (planCode === "biweekly")
    return 40 + Math.max(0, Math.max(1, bins) - 2) * EXTRA_BIN_PRICE;
  return 40 * Math.max(1, bins);
}

function planBreakdown(planCode, bins) {
  if (planCode === "onetime") return null;
  const extras = Math.max(0, Math.max(1, bins) - 2);
  if (extras === 0) return null;
  const base = planCode === "biweekly" ? 40 : 25;
  return `$${base} base + $8 × ${extras} extra bin${extras === 1 ? "" : "s"}`;
}

function nextCleaningDate(serviceDay) {
  // Returns next upcoming date matching the customer's service day.
  const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const target = dayMap[serviceDay] ?? 3;
  const today = new Date();
  const out = new Date(today);
  const delta = (target - today.getDay() + 7) % 7 || 7;
  out.setDate(today.getDate() + delta);
  return out.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

const STATUS_CONFIG = {
  active: { label: "Active", cls: "bg-accent-100 text-accent-700" },
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-700" },
  past_due: { label: "Past Due", cls: "bg-red-100 text-red-700" },
  canceled: { label: "Canceled", cls: "bg-slate-200 text-slate-600" },
  completed: { label: "Completed", cls: "bg-brand-100 text-brand-700" },
};

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "billing", label: "Plan & Billing", icon: CreditCard },
  { id: "history", label: "Service History", icon: Receipt },
  { id: "profile", label: "Profile", icon: UserIcon },
];

export default function Portal() {
  const { customer, refresh, signOut } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    api
      .get("/auth/me/jobs")
      .then(({ data }) => setJobs(data.jobs || []))
      .catch(() => setJobs([]));
  }, []);

  if (!customer) return null;

  const onSignOut = () => {
    signOut();
    toast.success("Signed out");
    navigate("/");
  };

  const firstName = customer.full_name.split(" ")[0] || customer.full_name;

  return (
    <div data-testid="page-portal" className="bg-brand-50/40 min-h-[calc(100vh-5rem)]">
      <section className="bg-white border-b border-slate-100">
        <div className="container-page py-6 md:py-8 lg:py-12">
          <p className="overline mb-1.5 lg:mb-3">My Portal</p>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-extrabold tracking-tight text-ink text-balance">
            Hi {firstName} <span className="inline-block">👋</span>
          </h1>
          <p className="mt-1.5 lg:mt-3 text-sm lg:text-base text-slate-600 max-w-2xl">
            Your bins are in good hands. Manage your plan, schedule, and photo
            proofs right here.
          </p>
        </div>
      </section>

      <section className="container-page py-5 md:py-6 lg:py-10">
        <div className="grid lg:grid-cols-12 gap-4 lg:gap-8">
          {/* Sidebar / Tabs */}
          <aside className="lg:col-span-3">
            <nav
              className="snap-tabs no-scrollbar flex lg:flex-col gap-1 lg:gap-2 overflow-x-auto lg:overflow-visible -mx-5 md:-mx-8 lg:mx-0 px-5 md:px-8 lg:px-0 pb-1 lg:pb-0 border-b border-slate-200 lg:border-b-0"
              aria-label="Portal sections"
              data-testid="portal-tabs"
            >
              {TABS.map((t) => {
                const active = tab === t.id;
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTab(t.id)}
                    aria-current={active ? "page" : undefined}
                    className={`shrink-0 lg:shrink inline-flex items-center gap-2 px-3 py-2 lg:rounded-xl lg:px-4 lg:py-3 text-sm font-semibold transition-all border-b-2 lg:border-b-0 lg:border ${
                      active
                        ? "text-brand-700 border-brand lg:bg-white lg:border-brand-100 lg:shadow-soft"
                        : "text-slate-600 border-transparent hover:text-ink lg:hover:bg-white lg:hover:border-transparent"
                    }`}
                    data-testid={`portal-tab-${t.id}`}
                  >
                    <Icon size={14} className="shrink-0" />
                    {t.label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={onSignOut}
                className="shrink-0 lg:shrink lg:mt-4 inline-flex items-center gap-2 px-3 py-2 lg:rounded-xl lg:px-4 lg:py-3 text-sm font-semibold text-slate-500 hover:text-red-600 lg:hover:bg-red-50 border-b-2 lg:border-b-0 border-transparent transition-colors"
                data-testid="portal-tab-signout"
              >
                <LogOut size={14} className="shrink-0" /> Sign Out
              </button>
            </nav>
          </aside>

          <main className="lg:col-span-9 space-y-4 lg:space-y-6 min-w-0 overflow-x-clip">
            {tab === "overview" && (
              <Overview customer={customer} jobs={jobs} />
            )}
            {tab === "billing" && (
              <PlanBilling customer={customer} refresh={refresh} />
            )}
            {tab === "history" && <ServiceHistory jobs={jobs} customer={customer} />}
            {tab === "profile" && (
              <Profile customer={customer} refresh={refresh} />
            )}
          </main>
        </div>
      </section>
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || {
    label: status || "Unknown",
    cls: "bg-slate-200 text-slate-600",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full ${cfg.cls}`}
      data-testid="portal-status-pill"
    >
      <Sparkles size={10} />
      {cfg.label}
    </span>
  );
}

function Overview({ customer, jobs }) {
  const planName = PLAN_LABEL[customer.plan_code] || customer.plan_code;
  const monthlyDollars = planMonthlyEstimate(customer.plan_code, customer.bin_count);
  const nextDate = nextCleaningDate(customer.service_day);
  const cadenceCopy =
    customer.plan_code === "biweekly"
      ? `Every other ${customer.service_day}`
      : customer.plan_code === "monthly"
        ? `Once a month on ${customer.service_day}`
        : `One-time clean on ${customer.service_day}`;

  return (
    <div className="space-y-4 lg:space-y-6 w-full min-w-0" data-testid="portal-overview">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-5">
        {/* Plan card */}
        <article
          data-reveal
          className="w-full max-w-full bg-white border border-slate-100 rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-soft"
          data-testid="portal-card-plan"
        >
          <div className="flex items-center justify-between mb-2 lg:mb-3">
            <span className="overline">Your Plan</span>
            <CreditCard size={14} className="text-brand" />
          </div>
          <h3 className="font-heading text-xl lg:text-2xl font-bold text-ink mb-0.5 lg:mb-1 leading-snug">
            {planName}
          </h3>
          <p className="text-[13px] lg:text-sm text-slate-500 mb-1 leading-snug">
            {customer.bin_count} {customer.bin_count === 1 ? "bin" : "bins"}
            {" · "}${monthlyDollars}
            {customer.plan_code === "onetime" ? " one-time" : " / mo"}
          </p>
          {planBreakdown(customer.plan_code, customer.bin_count) && (
            <p
              className="text-[11px] lg:text-xs text-slate-400 mb-3 lg:mb-4"
              data-testid="portal-plan-breakdown-overview"
            >
              {planBreakdown(customer.plan_code, customer.bin_count)}
            </p>
          )}
          {!planBreakdown(customer.plan_code, customer.bin_count) && (
            <div className="mb-3 lg:mb-4" />
          )}
          <button
            type="button"
            onClick={() =>
              window.dispatchEvent(new CustomEvent("portal-set-tab", { detail: "billing" }))
            }
            className="text-[13px] lg:text-sm font-bold text-brand-700 hover:text-brand inline-flex items-center gap-1"
            data-testid="portal-overview-manage-plan"
          >
            Manage plan →
          </button>
        </article>

        {/* Next cleaning card */}
        <article
          data-reveal
          style={{ "--reveal-delay": "80ms" }}
          className="w-full max-w-full bg-white border border-slate-100 rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-soft"
          data-testid="portal-card-next"
        >
          <div className="flex items-center justify-between mb-2 lg:mb-3">
            <span className="overline">Next Cleaning</span>
            <CalendarClock size={14} className="text-brand" />
          </div>
          <h3 className="font-heading text-xl lg:text-2xl font-bold text-ink mb-0.5 lg:mb-1 leading-snug">
            {nextDate}
          </h3>
          <p className="text-[13px] lg:text-sm text-slate-500 mb-3 lg:mb-4 leading-snug">{cadenceCopy}</p>
          <p className="inline-flex items-center gap-1.5 text-[11px] lg:text-xs text-slate-600">
            <Camera size={11} className="text-accent" />
            We'll send photos after.
          </p>
        </article>

        {/* Subscription card */}
        <article
          data-reveal
          style={{ "--reveal-delay": "160ms" }}
          className="w-full max-w-full bg-white border border-slate-100 rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-soft sm:col-span-2 lg:col-span-1"
          data-testid="portal-card-subscription"
        >
          <div className="flex items-center justify-between mb-2 lg:mb-3">
            <span className="overline">Subscription</span>
            <StatusPill status={customer.subscription_status} />
          </div>
          <h3 className="font-heading text-lg lg:text-xl font-bold text-ink mb-0.5 lg:mb-1 leading-snug">
            Card on file
          </h3>
          <p className="text-[13px] lg:text-sm text-slate-500 mb-3 lg:mb-4 leading-snug">
            Ending in •••• {customer.card_last4 || "----"}
          </p>
        </article>
      </div>

      {/* Recent service history */}
      <article
        className="w-full max-w-full bg-white border border-slate-100 rounded-xl lg:rounded-2xl p-5 lg:p-7 shadow-soft"
        data-testid="portal-recent-history"
      >
        {(() => {
          const completedJobs = jobs.filter((j) => j.status === "completed");
          const hasCompleted = completedJobs.length > 0;
          return (
            <>
              <div className="flex items-start justify-between mb-3 lg:mb-5 gap-4">
                <div>
                  <p className="overline mb-1 lg:mb-2">Recent Service History</p>
                  <h3 className="font-heading text-lg lg:text-xl font-bold text-ink">
                    {hasCompleted
                      ? "Last 2 cleanings"
                      : "Your first clean is coming up."}
                  </h3>
                </div>
              </div>

              {!hasCompleted ? (
                <div className="rounded-xl bg-brand-50 border border-brand-100 p-4 lg:p-5">
                  <p className="text-[13px] lg:text-sm text-slate-700 leading-relaxed">
                    No cleanings yet. Your first service is scheduled for{" "}
                    <span className="font-semibold text-ink">
                      {nextCleaningDate(customer.service_day)}
                    </span>
                    . You'll get a photo and a fresh receipt right after.
                  </p>
                </div>
              ) : (
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                  {completedJobs.slice(0, window.innerWidth >= 1024 ? 3 : 2).map((j) => {
                    const photo = (j.photos || [])[0];
                    return (
                      <li
                        key={j.id}
                        className="flex lg:block items-center gap-3 lg:gap-0 rounded-xl bg-slate-50 border border-slate-100 p-3 lg:p-4"
                        data-testid={`portal-overview-history-${j.id}`}
                      >
                        <div className="h-12 w-12 shrink-0 lg:h-auto lg:w-auto lg:aspect-[4/3] rounded-lg bg-slate-200 overflow-hidden lg:mb-3 relative">
                          {photo ? (
                            <img
                              src={absolutePhotoUrl(photo.url)}
                              alt="Cleaned bin photo proof"
                              className="absolute inset-0 h-full w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <ImageIcon size={16} className="text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] lg:text-xs text-slate-500 lg:mb-1">
                            {j.completed_at
                              ? new Date(j.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                              : j.scheduled_for}
                          </p>
                          <p className="text-[13px] lg:text-sm font-semibold text-ink capitalize">
                            {j.status || "completed"}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          );
        })()}
      </article>
    </div>
  );
}

function PlanBilling({ customer, refresh }) {
  const [plan, setPlan] = useState(customer.plan_code);
  const [bins, setBins] = useState(customer.bin_count);
  const [savingPlan, setSavingPlan] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const monthly = planMonthlyEstimate(plan, bins);
  const dirty = plan !== customer.plan_code || bins !== customer.bin_count;

  const switchPlan = async () => {
    if (!dirty) return;
    setSavingPlan(true);
    try {
      await api.post("/auth/me/plan", { plan_code: plan, bin_count: bins });
      await refresh();
      toast.success("Plan updated", {
        description: `Now on ${PLAN_LABEL[plan]} (${bins} bin${bins === 1 ? "" : "s"}).`,
      });
    } catch (err) {
      toast.error("Couldn't update plan", { description: formatApiError(err) });
    } finally {
      setSavingPlan(false);
    }
  };

  const cancel = async () => {
    setCanceling(true);
    try {
      await api.post("/auth/me/cancel");
      await refresh();
      setConfirming(false);
      toast.success("Subscription canceled", {
        description: "We'll email you a confirmation. Come back any time.",
      });
    } catch (err) {
      toast.error("Couldn't cancel", { description: formatApiError(err) });
    } finally {
      setCanceling(false);
    }
  };

  return (
    <div className="space-y-4 lg:space-y-6 w-full min-w-0" data-testid="portal-billing">
      <article className="w-full max-w-full bg-white border border-slate-100 rounded-xl lg:rounded-2xl p-5 lg:p-7 shadow-soft">
        <div className="flex items-start justify-between mb-4 lg:mb-5 gap-3">
          <div className="min-w-0">
            <p className="overline mb-1.5 lg:mb-2">Current plan</p>
            <h2 className="font-heading text-lg lg:text-2xl font-bold text-ink leading-snug">
              {PLAN_LABEL[customer.plan_code]} ·{" "}
              <span className="text-brand">
                ${planMonthlyEstimate(customer.plan_code, customer.bin_count)}
                {customer.plan_code === "onetime" ? "" : "/mo"}
              </span>
            </h2>
            <p className="text-[13px] lg:text-sm text-slate-500 mt-1 leading-snug">
              {customer.bin_count} {customer.bin_count === 1 ? "bin" : "bins"}{" "}
              · service day {customer.service_day}
              {planBreakdown(customer.plan_code, customer.bin_count) && (
                <span
                  className="block text-[11px] lg:text-xs text-slate-400 mt-0.5"
                  data-testid="portal-plan-breakdown-billing"
                >
                  {planBreakdown(customer.plan_code, customer.bin_count)}
                </span>
              )}
            </p>
          </div>
          <StatusPill status={customer.subscription_status} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5 mt-4 lg:mt-6">
          <div>
            <label
              htmlFor="switch-plan"
              className="block text-[10px] lg:text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1.5 lg:mb-2"
            >
              Switch plan
            </label>
            <select
              id="switch-plan"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="h-11 lg:h-12 w-full rounded-xl border-2 border-slate-200 bg-white px-3 lg:px-4 text-sm lg:text-base text-ink focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/15"
              data-testid="portal-switch-plan"
            >
              <option value="monthly">Monthly · $25/mo + $8/extra bin</option>
              <option value="biweekly">Bi-Weekly · $40/mo + $8/extra · 2 cleans/mo</option>
              <option value="onetime">One-Time · $40/bin</option>
            </select>
          </div>
          <div>
            <label
              htmlFor="switch-bins"
              className="block text-[10px] lg:text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1.5 lg:mb-2"
            >
              Bin count
            </label>
            <select
              id="switch-bins"
              value={bins}
              onChange={(e) => setBins(parseInt(e.target.value, 10))}
              className="h-11 lg:h-12 w-full rounded-xl border-2 border-slate-200 bg-white px-3 lg:px-4 text-sm lg:text-base text-ink focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/15"
              data-testid="portal-switch-bins"
            >
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>
                  {n} {n === 1 ? "bin" : "bins"}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className="mt-3 lg:mt-4 text-[13px] lg:text-sm text-slate-700">
          New estimate:{" "}
          <span className="font-bold text-ink">${monthly}</span>{" "}
          <span className="text-slate-500">
            {plan === "onetime" ? "one-time" : "/ mo"}
          </span>
          {planBreakdown(plan, bins) && (
            <span
              className="block text-[11px] lg:text-xs text-slate-500 mt-1"
              data-testid="portal-plan-breakdown-estimate"
            >
              {planBreakdown(plan, bins)}
            </span>
          )}
        </p>

        <div className="mt-5 lg:mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-2.5 lg:gap-3">
          {dirty ? (
            <>
              <button
                type="button"
                onClick={switchPlan}
                disabled={savingPlan}
                className="btn-primary w-full sm:w-auto"
                data-testid="portal-save-plan"
              >
                {savingPlan ? "Saving…" : "Save plan changes"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPlan(customer.plan_code);
                  setBins(customer.bin_count);
                }}
                className="btn-ghost w-full sm:w-auto"
                data-testid="portal-reset-plan"
              >
                Reset
              </button>
            </>
          ) : (
            <p
              className="inline-flex items-center gap-1.5 text-[12px] lg:text-xs text-slate-500"
              data-testid="portal-plan-up-to-date"
            >
              <Check size={12} className="text-accent" />
              Your plan is up to date.
            </p>
          )}
        </div>
      </article>

      {/* Payment method */}
      <article className="w-full max-w-full bg-white border border-slate-100 rounded-xl lg:rounded-2xl p-5 lg:p-7 shadow-soft">
        <p className="overline mb-3">Payment method</p>
        <div className="flex items-center gap-2 text-[14px] lg:text-base text-slate-700 font-semibold mb-1.5 lg:mb-2 min-w-0">
          <CreditCard size={16} className="text-brand shrink-0" />
          <span className="truncate">Card ending in •••• {customer.card_last4 || "----"}</span>
        </div>
      </article>

      {/* Cancel zone */}
      {customer.subscription_status !== "canceled" && (
        <article className="w-full max-w-full bg-white border border-red-100 rounded-xl lg:rounded-2xl p-5 lg:p-7 shadow-soft">
          <p className="overline text-red-600 mb-2 lg:mb-3">Danger zone</p>
          <h3 className="font-heading text-lg lg:text-xl font-bold text-ink mb-1.5 lg:mb-2 break-words">
            Cancel subscription
          </h3>
          <p className="text-[13px] lg:text-sm text-slate-600 mb-4 lg:mb-5 leading-relaxed break-words">
            Cancel any time. We'll stop future cleanings and email a confirmation.
          </p>
          {!confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="w-full lg:w-auto flex lg:inline-flex items-center justify-center rounded-xl border-2 border-red-200 bg-white px-5 h-11 lg:h-auto lg:py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 transition-colors break-words"
              data-testid="portal-cancel-button"
            >
              Cancel subscription
            </button>
          ) : (
            <div className="flex flex-col lg:flex-row lg:flex-wrap gap-2.5 lg:gap-3 w-full min-w-0">
              <button
                type="button"
                onClick={cancel}
                disabled={canceling}
                className="w-full lg:w-auto flex lg:inline-flex items-center justify-center rounded-xl bg-red-600 px-5 h-11 lg:h-auto lg:py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60 break-words"
                data-testid="portal-cancel-confirm"
              >
                {canceling ? "Canceling…" : "Yes, cancel my subscription"}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="btn-ghost w-full lg:w-auto"
                data-testid="portal-cancel-abort"
              >
                Keep my plan
              </button>
            </div>
          )}
        </article>
      )}
    </div>
  );
}

function ServiceHistory({ jobs, customer }) {
  return (
    <article
      className="w-full max-w-full bg-white border border-slate-100 rounded-xl lg:rounded-2xl p-5 lg:p-7 shadow-soft"
      data-testid="portal-history"
    >
      <p className="overline mb-2 lg:mb-3">Service history</p>
      <h2 className="font-heading text-xl lg:text-2xl font-bold text-ink mb-4 lg:mb-5">
        Past cleanings & photo proofs
      </h2>

      {jobs.length === 0 ? (
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-5 lg:p-6 text-center">
          <ImageIcon size={24} className="mx-auto text-slate-400 mb-2 lg:mb-3" />
          <p className="text-sm lg:text-base font-semibold text-ink mb-1">
            No cleanings yet
          </p>
          <p className="text-[13px] lg:text-sm text-slate-500">
            Your first service is scheduled for{" "}
            <span className="font-semibold text-ink">
              {nextCleaningDate(customer.service_day)}
            </span>
            . Photos and receipts will land here right after.
          </p>
        </div>
      ) : (
        <ul className="space-y-3 lg:space-y-4">
          {jobs.map((j) => {
            const photos = j.photos || [];
            const isCompleted = j.status === "completed";
            return (
              <li
                key={j.id}
                className="border border-slate-100 rounded-xl p-3.5 lg:p-5"
                data-testid={`portal-history-row-${j.id}`}
              >
                <div className="flex items-start gap-3 lg:gap-4">
                  <div className="h-14 w-14 lg:h-20 lg:w-20 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden relative shrink-0">
                    {photos[0] ? (
                      <img
                        src={absolutePhotoUrl(photos[0].url)}
                        alt="Cleaned bin proof"
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <ImageIcon size={18} className="text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] lg:text-base font-semibold text-ink capitalize">
                      {j.status || "scheduled"}
                    </p>
                    <p className="text-[11px] lg:text-xs text-slate-500">
                      {isCompleted && j.completed_at
                        ? `Completed ${new Date(j.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                        : `Scheduled ${j.scheduled_for}`}
                    </p>
                    {j.notes && (
                      <p className="text-[13px] lg:text-sm text-slate-600 mt-1.5 lg:mt-2 leading-relaxed">
                        {j.notes}
                      </p>
                    )}
                  </div>
                </div>
                {photos.length > 1 && (
                  <ul className="mt-2.5 lg:mt-3 grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-4 gap-1.5 lg:gap-2">
                    {photos.slice(1).map((p) => (
                      <li
                        key={p.id}
                        className="aspect-square rounded-md overflow-hidden bg-slate-100 relative"
                      >
                        <img
                          src={absolutePhotoUrl(p.url)}
                          alt="Cleaned bin"
                          className="absolute inset-0 h-full w-full object-cover"
                          loading="lazy"
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}

function Profile({ customer, refresh }) {
  const [form, setForm] = useState({
    full_name: customer.full_name,
    phone: customer.phone || "",
    address_line1: customer.address_line1,
    city: customer.city,
    state: customer.state,
    zip: customer.zip,
    service_day: customer.service_day,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const save = async (ev) => {
    ev.preventDefault();
    const errs = {};
    if (form.full_name.trim().length < 2) errs.full_name = "Tell us your name.";
    if (form.address_line1.trim().length < 2)
      errs.address_line1 = "Street address required.";
    if (form.city.trim().length < 2) errs.city = "City required.";
    if (!/^\d{5}$/.test(form.zip)) errs.zip = "5-digit zip.";
    if (!SERVICE_DAYS.includes(form.service_day)) errs.service_day = "Pick a day.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await api.patch("/auth/me", {
        full_name: form.full_name.trim(),
        phone: form.phone.trim() || null,
        address_line1: form.address_line1.trim(),
        city: form.city.trim(),
        state: form.state.trim().toUpperCase(),
        zip: form.zip,
        service_day: form.service_day,
      });
      await refresh();
      toast.success("Saved ✓");
    } catch (err) {
      toast.error("Couldn't save", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="w-full max-w-full bg-white border border-slate-100 rounded-xl lg:rounded-2xl p-5 lg:p-7 shadow-soft"
      data-testid="portal-profile"
      noValidate
    >
      <p className="overline mb-2 lg:mb-3">Profile</p>
      <h2 className="font-heading text-xl lg:text-2xl font-bold text-ink mb-1">
        Your details
      </h2>
      <p className="text-[13px] lg:text-sm text-slate-500 mb-5 lg:mb-6 leading-relaxed">
        Email is locked for v1. For changes, ping silkysmoothpowerwash@gmail.com.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 lg:gap-5">
        <ProfileField
          label="Email"
          id="profile-email"
          colspan="sm:col-span-6"
          testId="profile-email"
        >
          <input
            id="profile-email"
            value={customer.email}
            readOnly
            className="profile-input bg-slate-50 cursor-not-allowed text-slate-500"
            data-testid="profile-input-email"
          />
        </ProfileField>
        <ProfileField
          label="Full name"
          id="profile-full_name"
          error={errors.full_name}
          colspan="sm:col-span-3"
          testId="profile-full_name"
        >
          <input
            id="profile-full_name"
            value={form.full_name}
            onChange={(e) => update("full_name", e.target.value)}
            className="profile-input"
            data-testid="profile-input-name"
          />
        </ProfileField>
        <ProfileField
          label="Phone"
          id="profile-phone"
          colspan="sm:col-span-3"
          testId="profile-phone"
        >
          <input
            id="profile-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            className="profile-input"
            data-testid="profile-input-phone"
          />
        </ProfileField>
        <ProfileField
          label="Street address"
          id="profile-address"
          error={errors.address_line1}
          colspan="sm:col-span-6"
          testId="profile-address"
        >
          <input
            id="profile-address"
            value={form.address_line1}
            onChange={(e) => update("address_line1", e.target.value)}
            className="profile-input"
            data-testid="profile-input-address"
          />
        </ProfileField>
        <ProfileField
          label="City"
          id="profile-city"
          error={errors.city}
          colspan="sm:col-span-3"
          testId="profile-city"
        >
          <input
            id="profile-city"
            value={form.city}
            onChange={(e) => update("city", e.target.value)}
            className="profile-input"
            data-testid="profile-input-city"
          />
        </ProfileField>
        <ProfileField
          label="State"
          id="profile-state"
          colspan="sm:col-span-1"
          testId="profile-state"
        >
          <input
            id="profile-state"
            maxLength={2}
            value={form.state}
            onChange={(e) => update("state", e.target.value.toUpperCase())}
            className="profile-input"
            data-testid="profile-input-state"
          />
        </ProfileField>
        <ProfileField
          label="Zip"
          id="profile-zip"
          error={errors.zip}
          colspan="sm:col-span-2"
          testId="profile-zip"
        >
          <input
            id="profile-zip"
            inputMode="numeric"
            value={form.zip}
            onChange={(e) =>
              update("zip", e.target.value.replace(/\D/g, "").slice(0, 5))
            }
            className="profile-input"
            data-testid="profile-input-zip"
          />
        </ProfileField>
        <ProfileField
          label="Service day"
          id="profile-day"
          error={errors.service_day}
          colspan="sm:col-span-6"
          testId="profile-service_day"
        >
          <div
            className="grid grid-cols-3 sm:grid-cols-6 gap-2"
            role="radiogroup"
          >
            {SERVICE_DAYS.map((d) => {
              const selected = form.service_day === d;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => update("service_day", d)}
                  aria-pressed={selected}
                  className={`rounded-xl border-2 py-2 lg:py-2.5 text-[13px] lg:text-sm font-bold transition-all ${
                    selected
                      ? "border-brand bg-brand text-white shadow-soft"
                      : "border-slate-200 bg-white text-ink hover:border-brand/40"
                  }`}
                  data-testid={`profile-day-${d.toLowerCase()}`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </ProfileField>
      </div>

      <div className="mt-5 lg:mt-7 flex flex-col sm:flex-row sm:items-center gap-2.5 lg:gap-3">
        <button
          type="submit"
          disabled={saving}
          className="btn-primary w-full sm:w-auto disabled:opacity-60"
          data-testid="profile-save"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <p className="inline-flex items-center gap-1.5 text-[11px] lg:text-xs text-slate-500">
          <MapPin size={12} className="text-accent" />
          Service area limited to South Jersey routes.
        </p>
      </div>

      <style>{`
        .profile-input {
          width: 100%;
          height: 2.75rem;
          border-radius: 0.75rem;
          border: 2px solid #E2E8F0;
          background: #fff;
          padding: 0.5rem 0.875rem;
          font-size: 1rem;
          color: #0F172A;
          transition: border-color .2s, box-shadow .2s;
        }
        @media (min-width: 1024px) {
          .profile-input { height: 3rem; padding: 0.5rem 1rem; }
        }
        .profile-input:focus {
          outline: none;
          border-color: #0EA5E9;
          box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.15);
        }
      `}</style>
    </form>
  );
}

function ProfileField({ label, id, children, error, colspan, testId }) {
  return (
    <div className={colspan} data-testid={testId}>
      <label
        htmlFor={id}
        className="block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-2"
      >
        {label}
      </label>
      {children}
      {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
    </div>
  );
}
